// PaletteLegend — the canvas legend, as on the Miro board's "Legend & Reading
// Guide": one colour swatch per COADA NDD role, in the board's order, plus the
// terminal banner. Swatches come from styleResolver's NDD_PALETTE, so they
// always match the tiles. Roles present in the loaded topology are prominent;
// the rest are muted. Bottom-left HUD panel, sibling to the playbar; collapsible
// so it never has to cover canvas content (collapsed by default on small
// screens).

import { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectLegendEntries } from '../../store/slices/topologySlice.ts';
import { NDD_PALETTE } from '../../services/styleResolver.ts';

type PaletteKey = keyof typeof NDD_PALETTE;

/** The board's order and wording. */
const LEGEND_ROLES: { key: PaletteKey; label: string }[] = [
  { key: 'command', label: 'Command' },
  { key: 'event', label: 'Event' },
  { key: 'process', label: 'Policy / Process / Saga' },
  { key: 'projection', label: 'Projection' },
  { key: 'resolver', label: 'Resolver / Query' },
  { key: 'action', label: 'Action' },
  { key: 'rule', label: 'Business rule / Refusal' },
  { key: 'external', label: 'External system' },
  { key: 'interface', label: 'Interface' },
  { key: 'moment', label: 'Moment' },
  { key: 'dataStore', label: 'Data store' },
  { key: 'terminal', label: 'Terminal banner' },
];

/** Compact viewports start collapsed so the legend never covers the canvas. */
function startsCollapsed(): boolean {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 1280 || window.innerHeight < 600;
}

export default function PaletteLegend() {
  const entries = useSelector(selectLegendEntries);
  const [collapsed, setCollapsed] = useState(startsCollapsed);

  const presentFills = new Set(entries.map((e) => e.style.fill.toLowerCase()));

  return (
    <div className="fv2-legend" data-testid="palette-legend" role="group" aria-label="Legend">
      <button
        type="button"
        className="fv2-legend-head"
        aria-expanded={!collapsed}
        aria-label={collapsed ? 'Expand legend' : 'Collapse legend'}
        onClick={() => setCollapsed((c) => !c)}
      >
        <span>LEGEND</span>
        <span aria-hidden="true">{collapsed ? '▸' : '▾'}</span>
      </button>
      {!collapsed && (
        <ul className="fv2-legend-body" style={{ listStyle: 'none', margin: 0 }}>
          {LEGEND_ROLES.map(({ key, label }) => {
            const fill = NDD_PALETTE[key].fill;
            return (
              <li
                key={key}
                className="fv2-legend-item"
                data-present={presentFills.has(fill.toLowerCase())}
              >
                <span
                  className="fv2-legend-swatch"
                  data-testid={`legend-swatch-${key}`}
                  style={{ background: fill }}
                />
                <span>{label}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
